<template>
  <div class="room-selector" :set-options="options = create_options">
    <p class="room-selector__title">{{ selector_title }}</p>
    <div class="d-flex align-items-center">
      <div class="room-selector__price">
        <span class="room-selector__deal" v-if="product_info.product.deal && product_info.product.deal.tags.length">
          Ottimo prezzo!
        </span>
        <span class="room-selector__value">€ {{ options[1].value.live_price_no_tax }}</span>
        <span class="room-selector__tax">include gestione e assistenza</span>
      </div>
      <b-form-select
        class="room-selector__select"
        v-model="model_products[product_info.room_index][product_info.product_index].price"
        :options="options"
        @change="solution({
          room_index: product_info.room_index,
          product_index: product_info.product_index
        })"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { string, min, add, subtract, multiply } from "mathjs"
import { markup, round_up, get_live_price_no_tax } from '@bookingv3/utilities'

export default {
  name: 'room-selector',

  inject: ['details'],

  components: {
  },

  props: {
    product_info: Object
  },

  data() {
    return {
    }
  },

  computed: {
    ...mapState({
      // stato globale
      occupancy: state => state.occupancy,

      // stato del modulo livebkv3
      model_products: state => state.livebkv3.rooms_selection.model_products,
      product_quantities: state => state.livebkv3.rooms_selection.product_quantities,

      selected_solution: state => state.livebkv3.selected_solution,
    }),

    // Titolo della select
    selector_title() {
      return this.get_config('guiSettings.occupancy.maxRooms', 1) > 1 ? 'Quante camere?' : 'Scegli la camera'
    },

    // Genera le options delle select
    create_options() {
      const product = this.product_info.product,
            max_rooms = this.get_config('guiSettings.maxRooms', 1),
            options = [{ value: { qty: 0, base: 0, book: 0, total: 0, live_price: 0, live_price_no_tax: 0, extra_charges: {} }, text: '0' }], // default zero
            available_quantity = min(product.number_available_at_this_price, this.occupancy.length || max_rooms)

      // determina se la option è disabilitata
      const is_option_disabled = (qty) => {
        return this.product_quantities && this.product_quantities.hasOwnProperty(product.room)
               && qty > this.product_quantities[product.room][product.id]
      }

      // crea la struttura dati per gli extra charges con il valore total_amount
      const create_extra_charges = (product, qty) => {
        const extra_charges = product.price.extra_charges

        const parse_extra = (type) => type.filter(item => item.total_amount).map(item => ({
          charge: item.charge,
          total_amount: multiply(item.total_amount, qty)
        }))

        return Object.fromEntries(
          Object.entries(extra_charges).map(([key, value]) => [key, parse_extra(value)])
        )
      }

      // struttura delle option(con rispettivi value) calcolata sulla base della quantità disponibile e
      // il numero massimo di camere(available_quantity)
      const get_option = (qty) => {
        const base = multiply(product.price.base, qty),
              book = multiply(product.price.book, qty),
              total = multiply(product.price.total, qty),
              live_price = markup(book).live_price,
              extra_charges = create_extra_charges(product, qty),
              live_price_no_tax = get_live_price_no_tax(product.price, book, extra_charges) // live_price senza tasse di soggiorno

        return {
          value: {qty: qty, base: base, book: book, total: total, live_price: live_price, live_price_no_tax: live_price_no_tax, extra_charges: extra_charges},
          text: `${qty} (€ ${live_price_no_tax})`,
          disabled: is_option_disabled(qty),
        }
      }

      // genera le option
      for (let i = 1; i <= available_quantity; i++) {
        options.push(get_option(i))
      }

      return options
    },
  },

  created() {
    this.markup = markup
  },

  mounted() {
  },

  methods: {

    // Aggiorna le options delle select in base alle quantità scelte
    update_options(room, model_room) {

      const products_qty = {}, // traccia le quantità selezionate di ogni prodotto (per camera)
            occupancy_length = this.occupancy.length,
            update_quantities = this.product_quantities

      // popola 'products_qty' in base alle quantità scelte dall'utente di ogni prodotto.
      room.products.forEach((product, index) => {
        const room_id = product.room
        if (!products_qty[room_id]) {
          products_qty[room_id] = {}
        }
        // assegna la quantità selezionata di ogni prodotto (tramite id)
        products_qty[room_id][product.id] = model_room[index].price.qty
      })

      // popola 'options_products' che determina le opzioni disponibili per ogni camera e prodotto.
      room.products.forEach((product) => {
        const room_id = product.room
        if (!update_quantities[room_id]) {
          update_quantities[room_id] = {}
        }
        const available_qty = room.products[0].number_available_at_this_price
        // imposta la quantità disponibile per ogni prodotto, limitandola alla capacità di occupancy length
        update_quantities[room_id][product.id] = available_qty > occupancy_length ? occupancy_length : available_qty
      })

      // aggiorna 'options_products' in base alle quantità selezionate (contenute in 'products_qty')
      for (const room_id in update_quantities) {
        const room_options = update_quantities[room_id]
        for (const product_id in room_options) {
          if (products_qty[room_id]) {
            for (const qty_product_id in products_qty[room_id]) {
              if (product_id !== qty_product_id) {
                room_options[product_id] = subtract(room_options[product_id], products_qty[room_id][qty_product_id])
              }
            }
          }
        }
      }

      return update_quantities
    },

    // Gestisce la selezione, l'aggiornamento e la rimozione di prodotti nelle camere. Calcola i prezzi e aggiorna lo store
    update_solution(product) {

      // inizializza i prezzi di default
      const initialize_pricing = () => {
        return {base: 0, book: 0, total: 0, live_price: 0, live_price_no_tax: 0, extra_charges: {}}
      }

      // rimuove il prodotto dalla room
      const remove_product_from_room = (rooms, exist_room_index, id) => {
        if (exist_room_index !== -1) {
          let target_room = rooms[exist_room_index]
          delete target_room.products[id]
          if (Object.keys(target_room.products).length === 0) rooms.splice(exist_room_index, 1)
        }
      }

      // aggiunge il prodotto alla room
      const add_or_update_product = (rooms, exist_room_index, room, id, product) => {
        if (exist_room_index === -1) {
          rooms.push({
            id_room: room,
            name: this.details.rooms[this.product_info.room_index].name.it,
            products: { [id]: product }
          })
        } else {
          rooms[exist_room_index].products[id] = product
        }
      }

      // parsing degli extra_charges
      const parse_charges = (extra, target) => {
        if (extra) {
          extra.forEach(({ charge, total_amount }) => {
            let existing = target.find(e => e.charge === charge)
            if (existing) existing.total_amount = string(round_up(add(existing.total_amount, total_amount)))
            else target.push({ charge, total_amount })
          })
        }
        return target
      }

      // aggiornamento dei prezzi
      const update_pricing = (rooms, pricing, extra_charges_map) => {
        for (const room of rooms) {
          for (const product_id in room.products) {
            const selected_product = room.products[product_id].price_selected
            const extra_charges = selected_product.extra_charges

            for (const key in extra_charges) {
              if (!extra_charges_map[key]) extra_charges_map[key] = []
              extra_charges_map[key] = parse_charges(extra_charges[key], extra_charges_map[key])
            }

            pricing.base = string(round_up(add(pricing.base, selected_product.base)))
            pricing.book = string(round_up(add(pricing.book, selected_product.book)))
            pricing.total = string(round_up(add(pricing.total, selected_product.total)))
            pricing.live_price = string(round_up(add(pricing.live_price, selected_product.live_price)))
            pricing.extra_charges = extra_charges_map
            pricing.live_price_no_tax = string(get_live_price_no_tax(selected_product, pricing.book, pricing.extra_charges))
          }
        }
      }

      // creazione della struttura dati e aggiornamento dello store

      let rooms = this.selected_solution.rooms || [],
          pricing = initialize_pricing(),
          extra_charges_map = {}

      const {room, price_selected, id} = product,
            {qty} = price_selected

      let exist_room_index = rooms.findIndex(r => r.id_room === room)

      if (qty === 0) remove_product_from_room(rooms, exist_room_index, id)
      else if (qty > 0) add_or_update_product(rooms, exist_room_index, room, id, product)

      update_pricing(rooms, pricing, extra_charges_map)

      this.$store.commit('livebkv3/update_selected_solution', {
        rooms: rooms,
        pricing: pricing
      })
    },

    // Soluzione scelta
    solution(ids) {

      let model_room = this.model_products[ids.room_index],
          model_product = model_room[ids.product_index],
          room = this.details.rooms[ids.room_index],
          product = room.products[ids.product_index]

      this.$store.commit('livebkv3/update_rooms_selection', {
        product_quantities: this.update_options(room, model_room),
      })

      this.update_solution({...product, ...{price_selected: model_product.price}})
    }
  },
}
</script>

<style lang="scss" scoped>

  .room-selector {
    position: relative;
    margin-top: 15px;

    @include media-breakpoint-up(md){
      margin-top: 0;
    }

    &__title {
      font-size: $font-size-md;
      margin-bottom: 5px;
      text-align: right;

      @include media-breakpoint-up(md){
        margin-bottom: $grid-gutter-width/3;
      }
    }

    &__price {
      display: flex;
      flex-direction: column;
      flex-basis: 60%;
      text-align: right;
      margin-right: $grid-gutter-width/2;

      @include media-breakpoint-up(sm){
        flex-basis: 100%;
      }
    }

    &__deal {
      font-weight: normal;
      font-size: $font-size-md;
      color: $primary;
      margin: -10px 0 -7px;
    }

    &__value {
      font-size: $font-size-xl;
      color: $primary;
      font-weight: $vb-font-weight-semibold;
    }

    &__tax {
      font-size: $font-size-sm;
      color: $gray-500;
      cursor: default;
      line-height: normal;
    }

    &__select {
      flex-basis: 50%;
    }
  }

</style>